import { getMessenger } from "bootstrap/messengerFactory";
import { DialogMessenger } from "./dialogMessenger";
import { Environment } from "./environment";
import { logSentryError, SentryContext } from "utils/loggingUtil";
import { getUrlParameterByName } from "@yasoonofficial/runtime";
import { ViewMode } from "utils/enums";

export interface OfficeDialogProxyArguments {
    userProfile: Office.UserProfile;
    diagnostics: {
        hostName: string;
        hostVersion: string;
    };
    item: Office.Item;
    ewsUrl: string;
    restUrl: string;
    roamingSettings: { [id: string]: any; };
    yasoonIsSharedFolder: boolean;
    canUseCallbackToken: boolean;
}

export interface DialogMessage {
    type: 'callAsync' | 'close';
    callbackId?: string;
    methodName?: string;
    args?: any[];
}

export function secureAssign(baseObj: any, property: string, newValue: any) {
    try {
        baseObj[property] = newValue;
    } catch (e) {
        console.error('Could not assign property ' + property, e);
    }
}

export async function initProxy() {
    console.log('[OfficeDialogProxy] initProxy started');
    const dialogHandler: DialogMessenger = await getMessenger('dialog');
    const argsKey = getUrlParameterByName("argsKey");
    const viewMode = getUrlParameterByName("mode") as ViewMode;
    console.log('[OfficeDialogProxy] from URL:', { viewMode, argsKey }, dialogHandler.getName());
    const args: OfficeDialogProxyArguments = JSON.parse(await dialogHandler.getData(argsKey));
    if (!args) {
        console.error('[OfficeDialogProxy] No args received from Office!');
        throw new Error('We could not load data from Office');
    }

    //Start mocking the API - first, pocos
    if (!Office.context.ui)
        secureAssign(Office.context, 'ui', {});

    if (!Office.context.roamingSettings)
        secureAssign(Office.context, 'roamingSettings', {});

    //Diagnostics is usually there, but not on all platforms
    if (!Office.context.diagnostics) {
        secureAssign(Office.context, 'diagnostics', 'contextDiagnostics');
    }

    secureAssign(Office.context, 'mailbox', {});
    secureAssign(Office.context.mailbox, 'userProfile', args.userProfile);
    secureAssign(Office.context.mailbox, 'diagnostics', args.diagnostics);
    secureAssign(Office.context.mailbox, 'ewsUrl', args.ewsUrl);
    secureAssign(Office.context.mailbox, 'restUrl', args.restUrl);

    secureAssign(Office.context.mailbox, 'masterCategories', {});
    secureAssign(Office.context.mailbox.masterCategories, 'addAsync', (categories: Office.CategoryDetails[], callback?: (result: Office.AsyncResult<void>) => void): void => {

        doProxyCall('Office.context.mailbox.masterCategories.addAsync', callback, [categories]);
    });

    secureAssign(Office.context.mailbox, 'item', {});
    secureAssign(Office.context.mailbox.item, 'notificationMessages', {});
    secureAssign(Office.context.mailbox.item, 'categories', {});

    //We take over item generically
    const assignItem = (item: Office.Item) => {

        //When we are in firstLaunch-mode we have no item
        if (!item && viewMode === ViewMode.firstLaunch)
            return;

        try {
            Object.keys(item).forEach(key => {
                secureAssign(Office.context.mailbox.item, key, item[key]);
            });
        } catch (e) {
            logSentryError(e, SentryContext.elevatedPermission, { message: 'Error assigning Office item', item: item });
        }
    };

    assignItem(args.item);

    //Mock methods
    const doProxyCall = (methodName: string, callback: (result: Office.AsyncResult<any>) => void, callArgs: any[]): void => {
        dialogHandler.callAsyncMethod({
            methodName: methodName,
            args: callArgs
        }, callback);
    };

    secureAssign(Office.context.mailbox.item.notificationMessages, 'addAsync', (key: string, message: any, options?: any, callback?: (result: Office.AsyncResult<void>) => void): void => {
        if (options && typeof (options) === 'function') {
            callback = options;
            options = null;
        }

        doProxyCall('Office.context.mailbox.item.notificationMessages.addAsync', callback, (!!options) ? [key, message, options] : [key, message]);
    });

    secureAssign(Office.context.mailbox.item.categories, 'addAsync', (categories: string[], callback?: (result: Office.AsyncResult<void>) => void): void => {

        doProxyCall('Office.context.mailbox.item.categories.addAsync', callback, [categories]);
    });

    secureAssign(Office.context.mailbox.item.notificationMessages, 'removeAsync', (key: string, options?: any, callback?: (result: Office.AsyncResult<void>) => void): void => {
        if (options && typeof (options) === 'function') {
            callback = options;
            options = null;
        }

        doProxyCall('Office.context.mailbox.item.notificationMessages.removeAsync', callback, (!!options) ? [key, options] : [key]);
    });

    secureAssign(Office.context.mailbox.item.notificationMessages, 'getAllAsync', (options?: any, callback?: (result: Office.AsyncResult<void>) => void): void => {
        if (options && typeof (options) === 'function') {
            callback = options;
            options = null;
        }

        doProxyCall('Office.context.mailbox.item.notificationMessages.getAllAsync', callback, (!!options) ? [options] : []);
    });

    secureAssign(Office.context.mailbox.item.notificationMessages, 'replaceAsync', (key: string, message: any, options?: any, callback?: (result: Office.AsyncResult<void>) => void): void => {
        if (options && typeof (options) === 'function') {
            callback = options;
            options = null;
        }

        doProxyCall('Office.context.mailbox.item.notificationMessages.replaceAsync', callback, (!!options) ? [key, message, options] : [key, message]);
    });

    secureAssign(Office.context.mailbox.item, 'body', {
        getAsync: (coercionType: Office.CoercionType, options?: Office.AsyncContextOptions, callback?: (result: Office.AsyncResult<Office.Body>) => void) => {
            if (options && typeof (options) === 'function') {
                callback = options;
                options = null;
            }

            doProxyCall('Office.context.mailbox.item.body.getAsync', callback, (!!options) ? [coercionType, options] : [coercionType]);
        },
        getTextSelectionAsync: (coercionType: Office.CoercionType, options?: Office.AsyncContextOptions, callback?: (result: Office.AsyncResult<Office.Body>) => void) => {
            if (options && typeof (options) === 'function') {
                callback = options;
                options = null;
            }

            doProxyCall('Office.context.mailbox.item.body.getTextSelectionAsync', callback, (!!options) ? [coercionType, options] : [coercionType]);
        }
    });

    secureAssign(Office.context.mailbox.item, 'loadCustomPropertiesAsync', (callback?: (result: Office.AsyncResult<Office.CustomProperties>) => void, userContext?: any): void => {
        doProxyCall('Office.context.mailbox.item.loadCustomPropertiesAsync', callback, null);
    });

    if (Office.context.mailbox.item.itemType === "message") {
        secureAssign(Office.context.mailbox.item, 'getAllInternetHeadersAsync', (callback?: (result: Office.AsyncResult<string>) => void): void => {
            doProxyCall('Office.context.mailbox.item.getAllInternetHeadersAsync', callback, null);
        });
    }

    secureAssign(Office.context.mailbox.item, 'getAttachmentContentAsync', (attachmentId: string, options: Office.AsyncContextOptions, callback?: (result: Office.AsyncResult<Office.AttachmentContent>) => void, userContext?: any): void => {
        doProxyCall('Office.context.mailbox.item.getAttachmentContentAsync', callback, [attachmentId, options]);
    });

    secureAssign(Office.context.mailbox.item, 'getAsFileAsync', (options: Office.AsyncContextOptions, callback?: (result: Office.AsyncResult<Office.AttachmentContent>) => void, userContext?: any): void => {
        doProxyCall('Office.context.mailbox.item.getAsFileAsync', callback, [options]);
    });

    if (!Office.context.mailbox.item.itemId) {
        secureAssign(Office.context.mailbox.item, 'getItemIdAsync', (callback?: (result: Office.AsyncResult<string>) => void): void => {
            doProxyCall('Office.context.mailbox.item.getItemIdAsync', callback, null);
        });
    }

    if (Office.context.mailbox.item?.subject && typeof Office.context.mailbox.item.subject !== 'string') {
        secureAssign(Office.context.mailbox.item.subject, 'getAsync', (callback?: (result: Office.AsyncResult<string>) => void): void => {
            doProxyCall('Office.context.mailbox.item.subject.getAsync', callback, null);
        });
    }

    if (Environment.isWrapperAddin() || args.yasoonIsSharedFolder) {
        secureAssign(Office.context.mailbox.item, 'getSharedPropertiesAsync', (options: Office.AsyncContextOptions, callback?: (result: Office.AsyncResult<Office.SharedProperties>) => void): void => {
            doProxyCall('Office.context.mailbox.item.getSharedPropertiesAsync', callback, [options]);
        });
    }

    // Callback token will be deprecated in 10/2024
    if (Environment.isWrapperAddin() || args.canUseCallbackToken) {
        secureAssign(Office.context.mailbox, 'getCallbackTokenAsync', (...callbackArgs): void => {
            /* This method has 2 signatures... With or without options
             * options?: any, callback?: (result: Office.AsyncResult) => void, userContext?: any
             * callback?: (result: Office.AsyncResult) => void, userContext?: any
             * ATTENTION: userContext currently unsupported due to incompatible param order
            */
            let callback: () => any;
            let options: any[] = null;
            if (typeof callbackArgs[0] === 'function') {
                callback = callbackArgs[0];
            } else {
                options = [callbackArgs[0]];
                callback = callbackArgs[1];
            }

            doProxyCall('Office.context.mailbox.getCallbackTokenAsync', callback, options);
        });
    }

    //Code ripped from original function
    secureAssign(Office.context.mailbox, 'convertToRestId', (id) => {
        if (typeof id !== "string")
            return null;
        else
            return id.replace(new RegExp("[/]", "g"), "-").replace(new RegExp("[+]", "g"), "_");
    });

    secureAssign(Office.context.mailbox, 'convertToEwsId', (id) => {
        if (typeof id !== "string")
            return null;
        else
            return id.replace(new RegExp("[-]", "g"), "/").replace(new RegExp("[_]", "g"), "+");
    });

    // makeEwsRequestAsync will be deprecated together with callbacktoken in 10/2024
    if (Environment.isWrapperAddin() || args.canUseCallbackToken) {
        secureAssign(Office.context.mailbox, 'makeEwsRequestAsync', (data: any, callback?: (result: Office.AsyncResult<string>) => void, userContext?: any): void => {
            doProxyCall('Office.context.mailbox.makeEwsRequestAsync', callback, [data]);
        });
    }

    secureAssign(Office.context.mailbox, 'displayNewAppointmentForm', (data: Office.AppointmentForm) => {
        doProxyCall('Office.context.mailbox.displayNewAppointmentForm', () => { }, [data]);
    });

    secureAssign(Office.context.mailbox, 'displayNewMessageForm', (data: any) => {
        doProxyCall('Office.context.mailbox.displayNewMessageForm', () => { }, [data]);
    });

    secureAssign(Office.context.ui, 'displayDialogAsync', (startAddress: string, options?: Office.DialogOptions, callback?: (result: Office.AsyncResult<Office.Dialog>) => void): void => {
        doProxyCall('Office.context.ui.displayDialogAsync', callback, (!!options) ? [startAddress, options] : [startAddress]);
    });

    secureAssign(Office.context.ui, 'closeContainer', (callback?: (result: Office.AsyncResult<void>) => void): void => {
        doProxyCall('Office.context.ui.closeContainer', callback, null);
    });

    secureAssign(Office.context.mailbox, 'addHandlerAsync', (eventType: Office.EventType, handler?: (result: Office.Item) => void): void => {
        if (!Environment.isWrapperAddin())
            throw new Error('AddHandlerAsync via DialogProxy can only be called in COM - not dialogs');

        const wrappedHandler = (newItem: Office.Item) => {
            // Item changed. Invalidate this in Office API, then let app know about that
            assignItem(newItem);

            // Notify app
            handler(newItem);
        };

        doProxyCall('Office.context.mailbox.addHandlerAsync', null, [eventType, wrappedHandler]);
    });

    let roamingData = args.roamingSettings;
    secureAssign(Office.context.roamingSettings, 'get', (name: string): any => {
        let result = roamingData[name];
        return result;
    });
    secureAssign(Office.context.roamingSettings, 'set', (name: string, value: any): void => {
        roamingData[name] = value;
        doProxyCall('Office.context.roamingSettings.set', null, [name, value]);
    });

    secureAssign(Office.context.roamingSettings, 'saveAsync', (callback?: (result: Office.AsyncResult<void>) => void): void => {
        doProxyCall('Office.context.roamingSettings.saveAsync', async (result) => {
            //Reload settings
            let resultArgs = JSON.parse(await dialogHandler.getData(argsKey));
            if (resultArgs?.roamingSettings) {
                Object.keys(resultArgs.roamingSettings).forEach(key => {
                    roamingData[key] = resultArgs.roamingSettings[key];
                });
            }
            callback(result);
        }, null);
    });
}
